import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { Router, ActivatedRoute } from '@angular/router';

import { PageHeaderComponent } from '../../../shared/page-header/page-header.component';
import { MatInputComponent } from '../../../shared/mat-input/mat-input.component';
import { TripData } from '../../../models/billing.model';
import { RateCardService } from '../../../service/rate-card.service';
import { VendorService } from '../../../service/vendor.service';
import { AuthService } from '../../../auth/services/auth.service';

@Component({
  selector: 'app-trip-summary',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatTableModule,
    PageHeaderComponent,
    MatInputComponent,
  ],
  templateUrl: './trip-summary.component.html',
  styleUrls: ['./trip-summary.component.scss'],
})
export class TripSummaryComponent implements OnInit {
  selectedTrips: TripData[] = [];
  filters: any = {};
  vendorForm: FormGroup;
  isEditingVendor = false;
  originalVendorData: any = {};
  vendorDetails: any = null;
  loadingVendor = false;

  displayedColumns = [
    'sno',
    'routeName',
    'driver',
    'date',
    'vehicle',
    'distance',
    'rateCardType',
    'price',
    'cost',
  ];

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
    private rateCardService: RateCardService,
    private vendorService: VendorService,
    private authService: AuthService
  ) {
    this.vendorForm = this.fb.group({
      vendorName: ['', Validators.required],
      serviceType: ['', Validators.required],
      bankName: ['', Validators.required],
      accountNo: ['', Validators.required],
      ifscCode: ['', Validators.required],
      accountHolder: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    // Get data from router state using history.state
    const state = history.state;
    if (state && state.selectedTrips) {
      this.selectedTrips = state.selectedTrips || [];
      this.filters = state.filters || {};
      console.log('Data received from router state:', this.selectedTrips);
    } else {
      // Fallback: try to get from query params (for backward compatibility)
      this.route.queryParams.subscribe((params) => {
        const tripsData = params['trips'];
        if (tripsData) {
          try {
            this.selectedTrips = JSON.parse(decodeURIComponent(tripsData));
            console.log('Data received from query params:', this.selectedTrips);
          } catch (error) {
            console.error('Error parsing trips data:', error);
            this.selectedTrips = [];
          }
        }
      });
    }

    this.loadVendorDetails();
  }

  loadVendorDetails(): void {
    // Get vendor ID from the first trip (assuming all trips have same vendor)
    const firstTrip = this.selectedTrips[0];
    if (!firstTrip) return;

    const vendorId =
      firstTrip.vehicleDetails?.vendorId || firstTrip.driverDetails?.vendorId;

    if (!vendorId) {
      console.log('No vendor ID found in trips');
      return;
    }

    const jwt = this.authService.getToken();
    if (!jwt) {
      console.error('No JWT token found');
      return;
    }

    this.loadingVendor = true;
    this.vendorService.getVendorById(vendorId, jwt).subscribe({
      next: (response: any) => {
        this.vendorDetails = response.data || response;
        this.populateVendorForm();
        this.loadingVendor = false;
      },
      error: (error) => {
        console.error('Error fetching vendor details:', error);
        this.loadingVendor = false;
        // Keep form empty for manual editing
      },
    });
  }

  populateVendorForm(): void {
    if (this.vendorDetails) {
      this.vendorForm.patchValue({
        vendorName: this.vendorDetails.organizationName || '',
        serviceType: this.vendorDetails.vendorType || '',
        bankName: this.vendorDetails.bankDetails?.bankName || '',
        accountNo: this.vendorDetails.bankDetails?.accountNumber || '',
        ifscCode: this.vendorDetails.bankDetails?.ifscCode || '',
        accountHolder: this.vendorDetails.bankDetails?.accountHolderName || '',
      });
    }
  }

  get totalTrips(): number {
    return this.selectedTrips.length;
  }

  get totalDistance(): number {
    return this.selectedTrips.reduce((total, trip) => {
      // Use the actual distance from the API response
      const distance = trip.distance ? parseInt(trip.distance) : 0;
      return total + distance;
    }, 0);
  }

  get totalCost(): number {
    return this.selectedTrips.reduce((total, trip) => {
      // Extract cost from trip data
      const cost = trip.vehicleDetails?.cost || 0;
      return total + cost;
    }, 0);
  }

  editVendorDetails(): void {
    this.isEditingVendor = true;
    // Store original data for cancel functionality
    this.originalVendorData = this.vendorForm.value;
  }

  saveVendorDetails(): void {
    if (this.vendorForm.valid) {
      this.isEditingVendor = false;
      this.snackBar.open('Vendor details saved successfully', 'Close', {
        duration: 3000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
      });
    } else {
      this.snackBar.open('Please fill in all required fields', 'Close', {
        duration: 3000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
      });
    }
  }

  cancelEdit(): void {
    this.isEditingVendor = false;
    // Restore original data
    this.vendorForm.patchValue(this.originalVendorData);
  }

  getCurrentVendorDetails(): any {
    if (this.isEditingVendor || this.vendorForm.dirty) {
      // Return form data if editing or form has been modified
      const formValue = this.vendorForm.value;
      return {
        id: this.vendorDetails?.id || '',
        organizationName: formValue.vendorName,
        vendorType: formValue.serviceType,
        bankDetails: {
          bankName: formValue.bankName,
          accountNumber: formValue.accountNo,
          ifscCode: formValue.ifscCode,
          accountHolderName: formValue.accountHolder,
        },
      };
    } else {
      // Return original vendor details
      return this.vendorDetails;
    }
  }

  onProceedToBillGeneration(): void {
    // Get current vendor details (either from form or original data)
    const currentVendorDetails = this.getCurrentVendorDetails();

    // Navigate to bill generation with data in router state
    this.router.navigate(['/BillingManagement/bill-generation'], {
      state: {
        selectedTrips: this.selectedTrips,
        vendor: currentVendorDetails,
        filters: this.filters,
      },
    });
  }

  onBack(): void {
    // Navigate back to billing management
    this.router.navigate(['/BillingManagement']);
  }

  formatCurrency(amount: number): string {
    return `₹${amount.toLocaleString('en-IN')}`;
  }

  getRateCardTypeLabel(rateCardType?: string): string {
    if (!rateCardType) return 'N/A';
    const rateType = this.rateCardService
      .getRateTypes()
      .find((type) => type.value === rateCardType);
    return rateType ? rateType.label : rateCardType;
  }
}
